// solve.ts — direct, pure port of the prototype's solve() (Phase 0.3).
//
// PURE: World + Drivers in, Result out. No I/O, no clock, no globals, no
// mutation of inputs. This is what makes INV-1..6 testable. Do not add side
// effects here.

import type { BaseYields, Drivers, Tier, World } from "./entities.ts";

export interface HypResult {
  id: string;
  name: string;
  rating: string;
  capex: number;
  capexBase: number;
  aiRev: number;
  aiRevBase: number;
  profit: number;
  profitBase: number;
  dep: number;
  depBase: number;
  lockedDep: number;
  sig: number;
  totRev: number;
  wacd: number;
  wacd0: number;
  newDebt: number;
  interest: number;
  interest0: number;
  finDrag: number;
  spread: number;
}

export interface SupResult {
  t: Tier;
  n: string;
  rt: string;
  rev: number;
  revBase: number;
  margin: number;
  marginBase: number;
  profit: number;
  profitBase: number;
  dc: number;
  wacd: number;
  wacd0: number;
  spread: number;
  /** Revenue vs base as a fraction. INV-1: must be 0 in the base case. */
  pct: number;
}

export interface Result {
  labs: { openai: number; anthropic: number; other: number };
  hyp: HypResult[];
  sup: SupResult[];
  sysCapex: number;
  sysBase: number;
  cx: number;
}

/** Blended benchmark yield for a tenor weighting. Returns a decimal. */
export function bench(st: BaseYields, ten: [number, number, number]): number {
  return (ten[0] * st.y10 + ten[1] * st.y20 + ten[2] * st.y30) / 100;
}

export function solve(st: Drivers, w: World): Result {
  const { LABS, HYP, SUP, COEF, BASE_GAS, BASE_Y, TENOR } = w;

  const labs = {
    openai: LABS[0].compute * st.oa,
    anthropic: LABS[1].compute * st.an,
    other: COEF.otherLab.v * (0.45 + 0.55 * st.ai),
  };

  const hyp: HypResult[] = HYP.map((h) => {
    let sig: number;
    if (!h.aiRev) {
      sig = st.ai;
    } else {
      let ww = 0;
      for (const k of Object.keys(h.mix) as Array<keyof typeof labs>) {
        const base =
          k === "openai" ? LABS[0].compute : k === "anthropic" ? LABS[1].compute : COEF.otherLab.v;
        ww += (h.mix[k] as number) * (labs[k] / base);
      }
      sig = ww;
    }
    const aiRev = h.aiRev * sig;
    const wacd = bench(st, h.ten) + (h.spread * st.sm) / 10000;
    const wacd0 = bench(BASE_Y, h.ten) + h.spread / 10000;
    let finDrag = 1 - h.lev * (wacd - wacd0) * COEF.finDur.v;
    if (finDrag < 0.10) finDrag = 0.10; // INV-6
    const capex = h.capex * st.capex * (COEF.sticky.v + (1 - COEF.sticky.v) * sig) * finDrag;
    const dep =
      (h.prior * COEF.depShare.v) / st.life + ((capex * COEF.depShare.v) / st.life) * COEF.newInSvc.v;
    const depBase =
      (h.prior * COEF.depShare.v) / 6 + ((h.capex * COEF.depShare.v) / 6) * COEF.newInSvc.v;
    const newDebt = capex * h.debtShare;
    const interest = newDebt * wacd;
    const interest0 = h.capex * h.debtShare * wacd0;
    const profit =
      h.baseProfit +
      (aiRev - h.aiRev) * h.incM -
      (dep - depBase) -
      (interest - interest0) -
      h.totRev * 0.012 * (st.pw - 1);
    return {
      id: h.id,
      name: h.name,
      rating: h.rating,
      capex,
      capexBase: h.capex,
      aiRev,
      aiRevBase: h.aiRev,
      profit,
      profitBase: h.baseProfit,
      dep,
      depBase,
      lockedDep: (h.prior * COEF.depShare.v) / st.life,
      sig,
      totRev: h.totRev,
      wacd,
      wacd0,
      newDebt,
      interest,
      interest0,
      finDrag,
      spread: h.spread * st.sm,
    };
  });

  const sysCapex = hyp.reduce((a, h) => a + h.capex, 0);
  const sysBase = HYP.reduce((a, h) => a + h.capex, 0);
  const cx = sysCapex / sysBase;

  const gasMult = st.gas / BASE_GAS;
  const utilMult = (1 - st.gas) / (1 - BASE_GAS);

  const sup: SupResult[] = SUP.map((s) => {
    const dcPart = s.rev * s.dc;
    const other = s.rev * (1 - s.dc);
    const tilt = s.t === "gas" ? gasMult : s.t === "util" ? utilMult : 1;
    const resp = s.cu + (1 - s.cu) * cx * tilt;
    let rev = other * COEF.gdp.v + dcPart * resp;
    if (s.ps) rev *= 1 + (st.pw - 1) * s.ps;
    const ten = TENOR[s.t];
    const wacd = bench(st, ten) + (s.spread * st.sm) / 10000;
    const wacd0 = bench(BASE_Y, ten) + s.spread / 10000;
    const dRate = wacd - wacd0;
    let rateDrag = 1 - COEF.supRate.v * s.cap * dRate;
    if (rateDrag < 0.6) rateDrag = 0.6; // INV-6
    rev *= rateDrag;
    const volLev = 1 + s.lev * (rev / s.rev - 1);
    let margin = s.m * volLev - s.cs * (st.cost - 1) - s.cap * dRate * 1.6;
    if (margin < -0.3) margin = -0.3; // INV-6
    return {
      t: s.t,
      n: s.n,
      rt: s.rt,
      rev,
      revBase: s.rev,
      margin,
      marginBase: s.m,
      profit: rev * margin,
      profitBase: s.rev * s.m,
      dc: s.dc,
      wacd,
      wacd0,
      spread: s.spread * st.sm,
      pct: rev / s.rev - 1,
    };
  });

  return { labs, hyp, sup, sysCapex, sysBase, cx };
}
